/**
 * Subscription key format and parsing for SubscriptionProvider Option A.
 * Key = "channel:params" (e.g. priceFeed:0, orderbook:0, kline:0:1h, buckets).
 */

export type ParsedKeyType = "multiplex" | "buckets" | "multiFeed";

export interface ParsedSubscriptionKey {
  type: ParsedKeyType;
  streamType?: string;
  marketId?: string;
  interval?: string;
  subChannel?: string;
}

const MULTIPLEX_KEYS = ["lastPrice", "priceFeed", "markPrice", "orderbook", "trades"] as const;
const MULTI_FEED_SUB_CHANNELS = [
  "positions",
  "orders",
  "funding_rate",
  "deviation",
  "twap",
  "kline",
] as const;

/**
 * Parse a subscription key into type and params for doSubscribe.
 * Examples:
 *   lastPrice:0      -> { type: 'multiplex', streamType: 'lastPrice', marketId: '0' }
 *   priceFeed:0      -> { type: 'multiplex', streamType: 'priceFeed', marketId: '0' }
 *   orderbook:0      -> { type: 'multiplex', streamType: 'orderbook', marketId: '0' }
 *   buckets          -> { type: 'buckets' }
 *   positions:0      -> { type: 'multiFeed', subChannel: 'positions', marketId: '0' }
 *   kline:0:1h       -> { type: 'multiFeed', subChannel: 'kline', marketId: '0', interval: '1h' }
 */
export function parseSubscriptionKey(key: string): ParsedSubscriptionKey | null {
  if (!key || typeof key !== "string") return null;
  const trimmed = key.trim();
  if (!trimmed) return null;

  if (trimmed === "buckets") {
    return { type: "buckets" };
  }

  const parts = trimmed.split(":");
  if (parts.length === 2) {
    const [channel, marketId] = parts;
    if (MULTIPLEX_KEYS.includes(channel as (typeof MULTIPLEX_KEYS)[number])) {
      return {
        type: "multiplex",
        streamType: channel,
        marketId,
      };
    }
    if (MULTI_FEED_SUB_CHANNELS.includes(channel as (typeof MULTI_FEED_SUB_CHANNELS)[number])) {
      return {
        type: "multiFeed",
        subChannel: channel,
        marketId,
      };
    }
  }

  if (parts.length === 3 && parts[0] === "kline") {
    return {
      type: "multiFeed",
      subChannel: "kline",
      marketId: parts[1],
      interval: parts[2],
    };
  }

  return null;
}

/**
 * Build subscription key for multiplex stream.
 */
export function keyMultiplex(streamType: string, marketId: string): string {
  return `${streamType}:${marketId}`;
}

/**
 * Build subscription key for multi-feed sub-channel.
 */
export function keyMultiFeed(subChannel: string, marketId: string, interval?: string): string {
  if (subChannel === "kline" && interval) {
    return `kline:${marketId}:${interval}`;
  }
  return `${subChannel}:${marketId}`;
}
